import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { alpha, Grid, IconButton, styled, Tooltip } from '@mui/material';
import axios from '@/_utils/axios';
import { ElementInventoryStatus, FocusVersionScheduling, PolarisApiResponse } from '@/redux/slices/slice_types';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { LoadingStatuses, NAVBAR_HEIGHT, NAVBAR_HIDE_TRANSITION_DURATION } from '@/constants/constants';
import { selectIsAvailExpanded, selectSelectedAvailDetails, selectSelectedAvailId } from '@/redux/selectors/focusCalendarSelectors';
import { selectIsNavBarVisible } from '@/redux/selectors/layoutSelectors';
import ViewPolarisJobs from '@/components/Focus/AvailsSchedule/ViewPolarisJobsButton';
import Spinner from '../../../Spinner';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { loadAvailDetails, hideSidePanel, setIsAvailExpanded } from '@/redux/slices/focusCalendarSlice';
import AvailDetailsMainInfo from '@/components/_common/Table/Calendar/SidePanel/components/AvailDetailsMainInfo';
import ElementReadiness from '@/components/_common/Table/Calendar/SidePanel/components/ElementReadiness';
import { ReadinessElement } from '@/components/_common/Table/Calendar/Calendar.types';
import { useScrollGradient } from '@/components/_common/Table/Calendar/SidePanel/useScrollGradient.hook';
import { selectDataTableFilters } from '@/redux/selectors/dataTableSelectors';
import { FocusTheme } from '@/components/Focus/configs/theme';

const fetchSchedulingSearch = (schedulingIds: number[]) =>
  axios
    .post<PolarisApiResponse<FocusVersionScheduling[]>>('/focus/scheduling/search/', {
      id_filter: schedulingIds
    })
    .then((res) => res.data.data);

const SidePanel: FC = () => {
  const dispatch = useAppDispatch();
  const appliedFilters = useAppSelector(selectDataTableFilters);
  const tableLevelSelectedPlatforms: string[] | undefined = appliedFilters?.availed_platform_filter;
  const tableLevelSelectedStatuses: ElementInventoryStatus[] | undefined = appliedFilters?.status_filter;
  const isNavBarVisible = useAppSelector(selectIsNavBarVisible);
  const availId = useAppSelector(selectSelectedAvailId);
  const availDetails = useAppSelector(selectSelectedAvailDetails);
  const [schedulingData, setSchedulingData] = useState<FocusVersionScheduling[] | null>(null);
  const wrapperElRef = useRef<HTMLDivElement | null>(null);
  const innerWrapperElRef = useRef<HTMLDivElement | null>(null);

  const defaultElementFilters = {
    platform: tableLevelSelectedPlatforms ?? [],
    status: tableLevelSelectedStatuses ?? []
  };

  const elementsList = useMemo((): ReadinessElement[] => {
    if (!schedulingData) {
      return [];
    }

    return schedulingData.flatMap((schedule) =>
      schedule.elements.map(({ inventory_status, element: { element_type, element_subtype, language, video_resolution_type } }) => ({
        platform: schedule.availed_platform,
        inventory_status,
        element_type,
        element_subtype,
        language,
        video_resolution_type
      }))
    );
  }, [schedulingData]);

  const platformsList = useMemo(() => [...new Set(elementsList.map((el) => el.platform))], [elementsList]);

  useEffect(() => {
    if (!availDetails) {
      if (availId) {
        // Load data if not loaded or failed to load previously
        dispatch(loadAvailDetails(availId));
      }
      return;
    }

    if (availDetails.loadingStatus !== LoadingStatuses.fulfilled) return;

    fetchSchedulingSearch(availDetails.data.scheduling_ids).then(setSchedulingData);
  }, [availDetails]);

  const { isStartGradientVisible, isEndGradientVisible } = useScrollGradient(wrapperElRef, innerWrapperElRef);

  if (!availDetails) return null;

  return (
    <OuterWrapper>
      <Gradient direction="top-to-bottom" sx={{ opacity: isStartGradientVisible ? 1 : 0 }} />
      <Wrapper ref={wrapperElRef} isNavBarVisible={isNavBarVisible}>
        <div ref={innerWrapperElRef}>
          {availDetails.loadingStatus === LoadingStatuses.pending ? (
            <Spinner size={80} />
          ) : availDetails.loadingStatus === LoadingStatuses.rejected ? (
            availDetails.data?.message
          ) : availDetails.data ? (
            <>
              <ControlsRow schedule={availDetails.data} position="top" />
              <LayoutGrid container>
                <MainInfoWrapper item>
                  <AvailDetailsMainInfo details={availDetails.data} showPoster />
                </MainInfoWrapper>
                {!!elementsList.length && (
                  <ElementReadinessWrapper item>
                    <ControlsRow schedule={availDetails.data} position="right" />
                    <ElementReadiness
                      key={availDetails.data.id}
                      elements={elementsList}
                      loadedAt={new Date(availDetails.loadedAt)}
                      defaultSelectedFilters={defaultElementFilters}
                      platformsList={platformsList}
                    />
                  </ElementReadinessWrapper>
                )}
              </LayoutGrid>
            </>
          ) : null}
        </div>
      </Wrapper>
      <Gradient direction="bottom-to-top" sx={{ opacity: isEndGradientVisible ? 1 : 0 }} />
    </OuterWrapper>
  );
};

export default SidePanel;

const breakpoint = '872px';
const leftColumnWidth = '434px';

const OuterWrapper = styled('div')`
  position: relative;
`;

const Gradient = styled('div')<{ direction: 'top-to-bottom' | 'bottom-to-top' }>`
  position: absolute;
  left: 0;
  z-index: 1;
  width: 100%;
  height: 80px;
  pointer-events: none;
  transition: opacity 0.3s ease;

  ${({ theme, direction }) => `
    ${
      direction === 'top-to-bottom'
        ? `
          top: 0;
          border-top-left-radius: ${theme.shape.borderRadius * 2.4}px;
          border-top-right-radius: ${theme.shape.borderRadius * 2.4}px;
        `
        : `
          bottom: 0;
          border-bottom-left-radius: ${theme.shape.borderRadius * 2.4}px;
          border-bottom-right-radius: ${theme.shape.borderRadius * 2.4}px;
        `
    }
    background-image: linear-gradient(
      ${direction === 'top-to-bottom' ? 180 : 0}deg,
      ${(theme as FocusTheme).customOverrides.Calendar.backgroundDefault}FF 0%,
      ${(theme as FocusTheme).customOverrides.Calendar.backgroundDefault}00 100%
    );
  `}
`;

const Wrapper = styled('div')<{ isNavBarVisible: boolean }>`
  ${({ theme, isNavBarVisible }) => `
    padding: ${theme.spacing(2)};
    border-radius: ${theme.shape.borderRadius * 2.4}px;
    max-height: calc(100dvh - 20px * 2 - ${isNavBarVisible ? NAVBAR_HEIGHT : 0}px);
  `}

  transition: max-height ${NAVBAR_HIDE_TRANSITION_DURATION}ms ease; // This element's max-height depends on the navbar's visibility, that's why we use NAVBAR_HIDE_TRANSITION_DURATION
  background-color: ${({ theme }) => (theme as FocusTheme).customOverrides.Calendar.backgroundDefault};
  container-type: inline-size; // enables width-based queries
  overflow-y: auto;
`;

const LayoutGrid = styled(Grid)`
  flex-direction: column;
  flex-wrap: nowrap;
  gap: ${({ theme }) => theme.spacing(4)};

  @container (width > ${breakpoint}) {
    flex-direction: row;
    gap: ${({ theme }) => theme.spacing(3)};
  }
`;

const MainInfoWrapper = styled(Grid)`
  @container (width > ${breakpoint}) {
    flex-basis: ${leftColumnWidth};
    flex-shrink: 0;
  }
`;

const ElementReadinessWrapper = styled(Grid)`
  @container (width > ${breakpoint}) {
    flex-grow: 1;
  }
`;

interface ControlsRowProps {
  schedule: FocusVersionScheduling;
  position: 'top' | 'right';
}

const ControlsRow: FC<ControlsRowProps> = ({ schedule, position }) => {
  const dispatch = useAppDispatch();
  const isExpanded = useAppSelector(selectIsAvailExpanded);

  const handleCloseButtonClick = () => {
    dispatch(hideSidePanel());
  };

  const handleExpandButtonClick = () => {
    dispatch(setIsAvailExpanded(!isExpanded));
  };

  const handleRefresh = async () => {
    if (!schedule.id) return;
    await dispatch(loadAvailDetails(schedule.id)).unwrap();
  };

  return (
    <ControlsWrapper container sx={{ mb: 2.5 }} pos={position}>
      <Grid item xs>
        <ViewPolarisJobsAction variant="outlined" schedule={schedule} />
      </Grid>
      <Grid item>
        <Tooltip title="Refresh Content">
          <IconButton data-testid="refresh-side-panel-content" onClick={handleRefresh} size="small">
            <PolarisIcon icon="refresh" height={20} />
          </IconButton>
        </Tooltip>
        <IconButton onClick={handleExpandButtonClick}>
          <PolarisIcon icon={isExpanded ? 'minimize' : 'maximize'} />
        </IconButton>
        <IconButton onClick={handleCloseButtonClick}>
          <PolarisIcon icon="panel_close_right" />
        </IconButton>
      </Grid>
    </ControlsWrapper>
  );
};

const ControlsWrapper = styled(Grid)<{ pos: ControlsRowProps['position'] }>`
  ${({ pos }) => {
    if (pos === 'top') {
      return `
        @container (width > ${breakpoint}) {
          display: none;
        }
      `;
    } else if (pos === 'right') {
      return `
        @container (width <= ${breakpoint}) {
          display: none;
        }
      `;
    }
  }}
`;

const ViewPolarisJobsAction = styled(ViewPolarisJobs)`
  ${({ theme }) => {
    const actionColor = theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.palette.primary.dark;
    const hoverAlpha = theme.palette.mode === 'dark' ? 0.18 : 0.1;

    return `
      &.MuiButton-root {
        color: ${actionColor};
      }

      &.MuiButton-outlined {
        border-color: ${actionColor};
      }

      &.MuiButton-root:hover {
        background-color: ${alpha(actionColor, hoverAlpha)};
      }
    `;
  }}
`;
